import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetChequebooksQuery,
  useDeleteChequebookMutation,
  useGetBankAccountsQuery,
} from '../../../store/api/financeApi';

function ChequebooksList() {
  const navigate = useNavigate();
  const [bankAccountId, setBankAccountId] = useState('');
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const { data: books = [], isLoading } = useGetChequebooksQuery({
    bankAccountId: bankAccountId || undefined,
  });
  const [deleteChequebook] = useDeleteChequebookMutation();
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteChequebook(deleteId).unwrap();
      setDeleteId(null);
    } catch (err) {
      alert(err?.data?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Chequebooks
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/finance/chequebooks/new')}>
          Add Chequebook
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Bank account"
          value={bankAccountId}
          onChange={(e) => setBankAccountId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {bankAccounts.map((b) => (
            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Card>
        <CardContent>
          {isLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : books.length === 0 ? (
            <Typography color="text.secondary">No chequebooks. Create one (with leaf range) to issue cheques from it.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bank account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Leaves (range)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {books.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.bankAccount?.name || '–'}</TableCell>
                      <TableCell>{row.startLeaf} – {row.endLeaf} ({row.endLeaf - row.startLeaf + 1} leaves)</TableCell>
                      <TableCell>{row.status === 'active' ? 'Active' : 'Closed'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => navigate(`/finance/chequebooks/${row.id}`)}>
                          View
                        </Button>
                        <IconButton size="small" onClick={() => navigate(`/finance/chequebooks/${row.id}/edit`)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)} disabled={row.status === 'closed'}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete chequebook?</DialogTitle>
        <DialogContent>Only empty chequebooks can be deleted. If it has issued cheques, close it instead.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChequebooksList;
